import React, { useEffect, useReducer, useMemo, useCallback } from 'react';
import FiltrosGenerico from '../UI/FiltrosGenerico.js';
import ItemLista from '../UI/ItemLista.js';
import SelectPais from '../selectores/SelectPais.js';
import SelectProvincia from '../selectores/SelectProvincia.js';
import SelectLocalidad from '../selectores/SelectLocalidad.js';
import SelectClub from '../selectores/SelectClub.js';
import useSearchURL from '../../hooks/useSearchURL.js';
import { ARGENTINA } from '../constantes/Paises.js';
import * as Filtros from '../constantes/Filtros.js';
import * as Funciones from '../funciones/FuncionesFiltros.js';

const reducerFiltros = (state, action) => {
  return Funciones.ReducerFiltros(Filtros.ESTADIOS, state, action);
};

const FiltrosEstadios = (props) => {
  const searchParameters = props.searchParams;
  const [filtros, setFiltros] = useReducer(reducerFiltros, null);
  const { isBuscando, erroresSearch, searchData, resetErroresSearch } = useSearchURL();

  // Efecto para inicializar los filtros, vacío o con filtros del queryString
  useEffect(() => {
    if (searchParameters && searchParameters.size > 0) {
      const parametros = searchParameters.entries ? [...searchParameters.entries()] : [];
      const nuevoFiltro = Funciones.ArmarFiltroParametros(Filtros.ESTADIOS, parametros);
      setFiltros({ type: Funciones.Reducer.NUEVO_FILTRO, value: nuevoFiltro });
    } else {
      setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.ESTADIOS });
    }
  }, [searchParameters]);

  // Efecto separado para activar País inicialmente (después de INICIALIZAR)
  useEffect(() => {
    if (
      filtros &&
      filtros.pais &&
      !filtros.pais.estado &&
      (!searchParameters || searchParameters.size === 0)
    ) {
      // Activar País solo si no tiene parámetros de búsqueda
      setFiltros({ type: Funciones.Reducer.BOTON, value: Filtros.Estadio.PAIS });
    }
  }, [filtros, searchParameters]);

  const buscarEstadios = useCallback(
    async (filtros) => {
      setFiltros({ type: Funciones.Reducer.BUSCAR, value: false });
      resetErroresSearch();
      const bodyFiltros = Funciones.ArmarBodyFiltros(Filtros.ESTADIOS, filtros);
      if (bodyFiltros !== Funciones.ObjetoFiltrosVacio(Filtros.ESTADIOS)) {
        props.onBuscando(isBuscando);
        searchData('estadios/filtros', bodyFiltros, props.onListaCargada);
      }
    },
    [isBuscando, props, resetErroresSearch, searchData]
  );

  // Efecto para ejecutar la búsqueda cuando se activa el flag buscar
  useEffect(() => {
    if (filtros && filtros.buscar) {
      buscarEstadios(filtros);
    }
  }, [filtros, buscarEstadios]);

  const onFiltroElegido = useCallback((filtroElegido) => {
    setFiltros({ type: Funciones.Reducer.BOTON, value: filtroElegido });
  }, []);

  const onPaisElegido = useCallback((paisElegido) => {
    setFiltros({ type: Filtros.Estadio.PAIS, value: paisElegido });
  }, []);

  const onProvinciaElegida = useCallback((provinciaElegida) => {
    setFiltros({ type: Filtros.Estadio.PROVINCIA, value: provinciaElegida });
  }, []);

  const onLocalidadElegida = useCallback((localidadElegida) => {
    setFiltros({ type: Filtros.Estadio.LOCALIDAD, value: localidadElegida });
  }, []);

  const onClubElegido = useCallback((clubElegido) => {
    setFiltros({ type: Filtros.Estadio.CLUB, value: clubElegido });
  }, []);

  const handleBuscar = useCallback(
    (event) => {
      event?.preventDefault?.();
      const error = Funciones.ValidarFiltros(Filtros.ESTADIOS, filtros);
      if (error === '') {
        buscarEstadios(filtros);
      } else {
        alert(error);
      }
    },
    [filtros, buscarEstadios]
  );

  const handleLimpiar = () => {
    setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.ESTADIOS });
  };

  // Configuración de campos para el componente genérico
  const campos = useMemo(
    () => [
      {
        id: Filtros.Estadio.PAIS,
        nombre: 'País',
        activo: filtros?.pais?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Estadio.PAIS}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectPais paisActual={filtros.pais?.valor} onPaisElegido={onPaisElegido} />
        ),
      },
      {
        id: Filtros.Estadio.PROVINCIA,
        nombre: 'Provincia',
        activo: filtros?.provincia?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Estadio.PROVINCIA}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.pais?.estado && filtros?.pais?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectProvincia
            pais={filtros?.pais?.estado && filtros?.pais?.valor?.id ? filtros.pais.valor : null}
            provinciaActual={filtros.provincia?.valor}
            onProvinciaElegida={onProvinciaElegida}
          />
        ),
      },
      {
        id: Filtros.Estadio.LOCALIDAD,
        nombre: 'Localidad',
        activo: filtros?.localidad?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Estadio.LOCALIDAD}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={
              filtros?.pais?.estado &&
              ((filtros?.pais?.valor?.id && filtros?.pais?.valor.id !== ARGENTINA) ||
                (filtros?.provincia?.estado && filtros?.provincia?.valor?.id))
            }
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectLocalidad
            pais={filtros?.pais?.estado && filtros?.pais?.valor?.id ? filtros.pais.valor : null}
            provincia={
              filtros?.provincia?.estado && filtros?.provincia?.valor?.id
                ? filtros.provincia.valor
                : null
            }
            localidadActual={filtros.localidad?.valor}
            onLocalidadElegida={onLocalidadElegida}
          />
        ),
      },
      {
        id: Filtros.Estadio.CLUB,
        nombre: 'Club',
        activo: filtros?.club?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Estadio.CLUB}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.pais?.estado && filtros?.pais?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectClub
            pais={filtros?.pais?.estado && filtros?.pais?.valor?.id ? filtros.pais.valor : null}
            provincia={
              filtros?.provincia?.estado && filtros?.provincia?.valor?.id
                ? filtros.provincia.valor
                : null
            }
            localidad={
              filtros?.localidad?.estado && filtros?.localidad?.valor?.id
                ? filtros.localidad.valor
                : null
            }
            clubActual={filtros.club?.valor}
            onClubElegido={onClubElegido}
          />
        ),
      },
    ],
    [filtros, onFiltroElegido, onPaisElegido, onProvinciaElegida, onLocalidadElegida, onClubElegido]
  );

  if (!filtros) {
    return null;
  }

  return (
    <FiltrosGenerico
      filtros={filtros}
      campos={campos}
      onFiltroElegido={onFiltroElegido}
      onBuscar={handleBuscar}
      onLimpiar={handleLimpiar}
      isBuscando={isBuscando}
      errores={erroresSearch}
      titulo="Buscar Estadios"
      collapsibleEnMobile={true}
      initialCollapsed={props.initialCollapsed ?? false}
      hasResults={props.hasResults ?? false}
    />
  );
};

export default FiltrosEstadios;
